// POPaymentBadgeCell — extracted from AccountingPage.js (Feb-2026 monolith split).
// Lives in the PO table's Payment column. Renders the current lifecycle
// state (Paid / Pending / Overdue / Cancelled) and, when the user has edit
// rights, opens a tiny popover to change it in-place. Powered by:
//   PATCH /api/admin/accounting/purchase-orders/{po_id}/payment
// which enforces the approval gate — Paid requires approval_status=approved.
import React from 'react';
import { toast } from 'sonner';
import { ChevronDown, Paperclip } from 'lucide-react';
import { adminApi } from '../../lib/adminAuth';

export default function POPaymentBadgeCell({ po, canEdit, onUpdated, formatDate, onOpenProofs }) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const ref = React.useRef(null);
  const status = (po.status || '').toLowerCase();
  const isPaid = status === 'paid' || status === 'completed';
  const isCancelled = status === 'cancelled' || status === 'rejected';
  let due = null;
  try { if (po.due_date) due = new Date(po.due_date); } catch { due = null; }
  const isOverdue = !isPaid && !isCancelled && due && !isNaN(due) && due < new Date();
  const isApproved = po.approval_status === 'approved';

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const badge = (() => {
    if (isCancelled) return { cls: 'bg-neutral-100 text-neutral-600 border-neutral-200', label: `✕ ${status === 'rejected' ? 'Rejected' : 'Cancelled'}`, tip: po.cancelled_at ? `Cancelled on ${formatDate(po.cancelled_at)}` : 'Cancelled' };
    if (isPaid)     return { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: '● Paid', tip: po.paid_at ? `Paid on ${formatDate(po.paid_at)}` : 'Marked paid' };
    if (isOverdue)  {
      const days = Math.floor((new Date() - due) / (1000 * 60 * 60 * 24));
      return { cls: 'bg-red-100 text-red-800 border-red-200', label: `⚠ Overdue${days > 0 ? ` · ${days}d` : ''}`, tip: `Overdue by ${days} day${days !== 1 ? 's' : ''}` };
    }
    return { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: '○ Pending', tip: 'Not yet paid' };
  })();

  const doAction = async (action) => {
    if (saving) return;
    if (action === 'mark_paid' && !isApproved) {
      toast.error(`PO ${po.po_number || ''} must be approved before it can be marked paid.`);
      return;
    }
    const label = { mark_paid: 'Mark PAID', mark_pending: 'Revert to PENDING', mark_cancelled: 'CANCEL this PO' }[action];
    if (!window.confirm(`${label} — ${po.po_number || 'this PO'}?`)) return;
    setSaving(true);
    try {
      const r = await adminApi.patch(`/api/admin/accounting/purchase-orders/${po._id}/payment`, { action });
      onUpdated?.(r.data.po);
      toast.success(`${po.po_number || 'PO'} updated`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  if (!canEdit) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border ${badge.cls}`}
        data-testid={`po-payment-${po._id}`} title={badge.tip}>
        {badge.label}
      </span>
    );
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={saving}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border cursor-pointer hover:brightness-95 active:scale-95 transition ${badge.cls} ${saving ? 'opacity-60' : ''}`}
        data-testid={`po-payment-${po._id}`}
        title={`${badge.tip} · click to change`}
      >
        {badge.label}
        <ChevronDown className="w-3 h-3 -mr-0.5" />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 right-0 w-56 bg-white rounded-xl border border-neutral-200 shadow-2xl overflow-hidden" data-testid={`po-payment-menu-${po._id}`}>
          <div className="px-3 py-2 border-b border-neutral-100 bg-neutral-50">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">Change payment status</p>
            {!isApproved && (
              <p className="text-[10px] text-fuchsia-700 mt-0.5">⚠ Approve this PO first before marking paid</p>
            )}
          </div>
          <button
            onClick={() => doAction('mark_paid')}
            disabled={isPaid || saving || !isApproved}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`po-payment-action-paid-${po._id}`}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            Mark as Paid
            {isPaid && <span className="ml-auto text-[10px] text-emerald-700">current</span>}
          </button>
          <button
            onClick={() => doAction('mark_pending')}
            disabled={(!isPaid && !isCancelled) || saving}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`po-payment-action-pending-${po._id}`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
            Revert to Pending
            {!isPaid && !isCancelled && <span className="ml-auto text-[10px] text-amber-700">current</span>}
          </button>
          <div className="border-t border-neutral-100" />
          <button
            onClick={() => doAction('mark_cancelled')}
            disabled={isCancelled || saving}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`po-payment-action-cancel-${po._id}`}
          >
            <span className="w-2 h-2 rounded-full bg-neutral-500 inline-block" />
            Cancel this PO
            {isCancelled && <span className="ml-auto text-[10px] text-neutral-600">current</span>}
          </button>
          {onOpenProofs && (
            <>
              <div className="border-t border-neutral-100" />
              <button
                onClick={() => { setOpen(false); onOpenProofs(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-fuchsia-50"
                data-testid={`po-payment-action-proof-${po._id}`}
              >
                <Paperclip className="w-3.5 h-3.5 text-fuchsia-600" />
                {po.payment_proofs?.length ? `Payment proofs (${po.payment_proofs.length})` : 'Attach payment proof'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
